export const transformDataForChart = (backendData: any) => {
    if (!backendData?.datasets || !Array.isArray(backendData.datasets)) {
        return [];
    }
    const timestampMap = new Map();

    // Collect all unique timestamps and their values for each metric
    backendData.datasets.forEach((dataset: any) => {
        if (dataset.data && Array.isArray(dataset.data)) {
            dataset.data.forEach((point: any) => {
                const timestampValue = new Date(point.timestamp).getTime(); // Convert to number
                if (!timestampMap.has(timestampValue)) {
                    // Ensure 'timestamp' property is named consistently for XAxis dataKey
                    timestampMap.set(timestampValue, { timestamp: timestampValue });
                }
                timestampMap.get(timestampValue)[dataset.id] = point.value;

                const pointData = timestampMap.get(timestampValue);

                // Special handling for alarms to group them at the same timestamp
                if (dataset.id === 'alarms') {
                    if (!pointData.alarms) {
                        pointData.alarms = [];
                    }
                    if (point.value && typeof point.value === 'string') {
                        pointData.alarms.push(point.value);
                    }
                } else {
                    pointData[dataset.id] = point.value;
                }
            });
        }
    });

    // Convert map to array and sort by timestamp (which are now numbers)
    return Array.from(timestampMap.values()).sort((a, b) => a.timestamp - b.timestamp);
};

export const formatXAxisTick = (tickItem: number) => { // tickItem is now a number
    const date = new Date(tickItem);
    // You can customize date formatting as needed, e.g., to include time if relevant
    return date.toLocaleDateString(undefined, { month: 'numeric', day: 'numeric', year: '2-digit' });
};

export const formatTooltipValue = (value: any, name: string, getMetricInfo: (metricKey: string) => { label: string; color: string; unit: string }) => {
    const metricInfo = getMetricInfo(name);
    if (typeof value === 'number') {
        if (metricInfo.unit === 'boolean' || name.includes('output') || name.includes('Available')) {
            return value === 1 ? 'ON' : 'OFF';
        }
        return `${value.toFixed(2)} ${metricInfo.unit}`;
    }
    return value;
};

// Process continuous alarm periods from chart data
export const processAlarmPeriods = (chartData: any[]) => {
    const alarmPeriods: Array<{
        alarm: string;
        startTime: number;
        endTime: number;
    }> = [];

    const currentAlarms: Record<string, number> = {};

    // Iterate through each data point in time order
    chartData.forEach((dataPoint) => {
        const timestamp = dataPoint.timestamp;
        const alarms = dataPoint.alarms || [];

        // Check each active alarm
        Object.keys(currentAlarms).forEach(alarmText => {
            // If this alarm is no longer present in the current dataPoint
            if (!alarms.includes(alarmText)) {
                // End the period for this alarm
                alarmPeriods.push({
                    alarm: alarmText,
                    startTime: currentAlarms[alarmText],
                    endTime: timestamp,
                });
                // Remove from tracking
                delete currentAlarms[alarmText];
            }
        });

        // Check for new alarms
        alarms.forEach((alarmText: string) => {
            // If this is a new alarm we're not already tracking
            if (!currentAlarms[alarmText]) {
                // Start tracking this alarm
                currentAlarms[alarmText] = timestamp;
            }
        });
    });

    // Handle any alarms that are still active at the end of the data
    const lastTimestamp = chartData.length > 0 ? chartData[chartData.length - 1].timestamp : null;
    if (lastTimestamp) {
        Object.keys(currentAlarms).forEach(alarmText => {
            alarmPeriods.push({
                alarm: alarmText,
                startTime: currentAlarms[alarmText],
                endTime: lastTimestamp,
            });
        });
    }

    return alarmPeriods;
};